import { Button, Flex, SimpleGrid, Text, Icon, Input } from "@chakra-ui/react";
import { ArrowBackIcon, ArrowUpIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useFormik } from "formik";
import { FormWrapper } from "@/components/FormWrapper/FormWrapper";
import { BaseInput } from "@/components/Controls/BaseInput";
import { BaseSelect, SelectOption } from "@/components/Controls/BaseSelect";
import { BaseTextArea } from "@/components/Controls/BaseTextArea";
import { BaseControl } from "@/components/Controls/BaseControl";
import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { Recruiter } from "@/model/recruiters";
import { mockPartners, Partner } from "../PartnersPage/mockData";

interface PartnerForm {
  name: string;
  contacts: string | null;
  responsible: number | null;
  status: string | null;
  work_phone: string;
  work_email: string;
  website: string;
  inn: string;
  employees_count: string;
  legal_name: string;
  legal_address: string;
  mailing_address: string;
  director_last_name: string;
  director_first_name: string;
  director_middle_name: string;
  nda_blank_file: File | null;
  nda_scan_file: File | null;
  signed_in_edo: string | null;
  interaction_format: string | null;
  comment: string;
}

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("7")) {
    const phoneDigits = digits.slice(1);
    if (phoneDigits.length === 0) return "+7 (";
    if (phoneDigits.length <= 3) return `+7 (${phoneDigits}`;
    if (phoneDigits.length <= 6) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
    if (phoneDigits.length <= 8) return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
    return `+7 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 8)}-${phoneDigits.slice(8, 10)}`;
  }

  if (digits.length <= 1) return `+7 (${digits}`;
  if (digits.length <= 4) return `+7 (${digits}`;
  if (digits.length <= 7) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 9) return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
};

const statusOptions: SelectOption[] = [
  { value: "Активный", label: "Активный" },
  { value: "В работе", label: "В работе" },
  { value: "Приостановлен", label: "Приостановлен" },
  { value: "На согласовании", label: "На согласовании" },
];

const signedInEdoOptions: SelectOption[] = [
  { value: "Да", label: "Да" },
  { value: "Нет", label: "Нет" },
];

const interactionFormatOptions: SelectOption[] = [
  { value: "Регулярные встречи", label: "Регулярные встречи" },
  { value: "Email-переписка", label: "Email-переписка" },
  { value: "Телефонные звонки", label: "Телефонные звонки" },
  { value: "Видеоконференции", label: "Видеоконференции" },
  { value: "Мессенджеры", label: "Мессенджеры" },
];

const parseDirectorName = (fullName: string | undefined): { last: string; first: string; middle: string } => {
  if (!fullName) return { last: "", first: "", middle: "" };
  const parts = fullName.trim().split(/\s+/);
  return {
    last: parts[0] || "",
    first: parts[1] || "",
    middle: parts[2] || "",
  };
};

export const UpdatePartnerPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [recruiters, setRecruiters] = useState<readonly Recruiter[]>([]);
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPartner = () => {
      setIsLoading(true);
      const partner = mockPartners.find((p) => p.id === Number(id));
      if (partner) {
        setCurrentPartner(partner);
      }
      setIsLoading(false);
    };
    loadPartner();
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    api.recruiters
      .recruiters({})
      .then(({ data }) => {
        if (isMounted) setRecruiters(data || []);
      })
      .catch(() => {
        if (isMounted) setRecruiters([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const recruiterOptions: SelectOption[] = useMemo(() => {
    return recruiters.map((recruiter) => ({
      value: recruiter.id,
      label: recruiter.username || `Рекрутер #${recruiter.id}`,
    }));
  }, [recruiters]);

  const initialPartnerValues = useMemo(() => {
    if (!currentPartner) return null;

    const directorName = parseDirectorName(currentPartner.director_name);
    const responsibleId =
      recruiters.find((r) => r.username === currentPartner.responsible || r.username?.toLowerCase().includes(currentPartner.responsible.toLowerCase()))?.id || null;

    return {
      name: currentPartner.name ?? "",
      contacts: currentPartner.contacts || null,
      responsible: responsibleId,
      status: currentPartner.status || null,
      work_phone: currentPartner.work_phone || "",
      work_email: currentPartner.work_email || "",
      website: currentPartner.website || "",
      inn: currentPartner.inn || "",
      employees_count: currentPartner.employees_count || "",
      legal_name: currentPartner.legal_name || "",
      legal_address: currentPartner.legal_address || "",
      mailing_address: currentPartner.mailing_address || "",
      director_last_name: directorName.last,
      director_first_name: directorName.first,
      director_middle_name: directorName.middle,
      nda_blank_file: null,
      nda_scan_file: null,
      signed_in_edo: currentPartner.signed_in_edo ? "Да" : "Нет",
      interaction_format: currentPartner.interaction_format || null,
      comment: currentPartner.comment || "",
    };
  }, [currentPartner, recruiters]);

  const formik = useFormik<PartnerForm>({
    initialValues: {
      name: "",
      contacts: null,
      responsible: null,
      status: null,
      work_phone: "",
      work_email: "",
      website: "",
      inn: "",
      employees_count: "",
      legal_name: "",
      legal_address: "",
      mailing_address: "",
      director_last_name: "",
      director_first_name: "",
      director_middle_name: "",
      nda_blank_file: null,
      nda_scan_file: null,
      signed_in_edo: null,
      interaction_format: null,
      comment: "",
    },
    validate: (values) => {
      const errors: Partial<Record<keyof PartnerForm, string>> = {};
      if (!values.name.trim()) {
        errors.name = "Обязательное поле";
      }
      if (!values.responsible) {
        errors.responsible = "Обязательное поле";
      }
      return errors;
    },
    validateOnMount: true,
    onSubmit: () => {
      console.log("Обновление партнера:", formik.values);
    },
  });

  useEffect(() => {
    if (initialPartnerValues && !formik.dirty) {
      formik.resetForm({ values: initialPartnerValues });
    }
  }, [initialPartnerValues]);

  const hasChanges = useMemo(() => {
    if (!initialPartnerValues) return false;

    const current = formik.values;
    const initial = initialPartnerValues;

    if (current.nda_blank_file || current.nda_scan_file) {
      return true;
    }

    const fieldsToCheck = Object.keys(initial).filter((key) => key !== "nda_blank_file" && key !== "nda_scan_file") as Array<keyof PartnerForm>;

    return fieldsToCheck.some((field) => {
      const currentValue = current[field];
      const initialValue = initial[field];
      return currentValue !== initialValue;
    });
  }, [formik.values, initialPartnerValues]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    formik.setFieldValue("work_phone", formatted);
  };

  const handleFileChange = (fieldName: "nda_blank_file" | "nda_scan_file", e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    formik.setFieldValue(fieldName, file);
  };

  const isFormValid = formik.isValid && formik.values.name.trim() && formik.values.responsible;

  if (isLoading) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Загрузка...</Text>
        </Flex>
      </Layout>
    );
  }

  if (!currentPartner) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text>Партнер не найден</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate(`/partners/${id}`)} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />
          К партнеру
        </Text>

        <Flex flexDirection="column" gap="4px">
          <Text fontSize={24} fontWeight={500}>
            Редактирование партнера
          </Text>
          <Text color="gray.500">Обновите информацию о партнере</Text>
        </Flex>

        <FormWrapper>
          <Flex flexDirection="column" gap="24px">
            <Text fontSize={18} fontWeight={600} color="#3b515e">
              Основная информация
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <BaseInput
                fieldTitle="Название"
                requiredField={true}
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Введите название партнера"
                name="name"
                errorText={formik.errors.name}
                isEmpty={!formik.values.name.trim() && formik.touched.name}
              />

              <BaseSelect
                fieldTitle="Ответственный"
                requiredField={true}
                options={recruiterOptions}
                placeholder="Выберите ответственного"
                value={formik.values.responsible}
                onChange={(value) => formik.setFieldValue("responsible", value)}
                errorText={formik.errors.responsible}
                isEmpty={!formik.values.responsible && formik.touched.responsible}
              />

              <BaseSelect
                fieldTitle="Контакты"
                options={[]}
                placeholder="Выберите контакт"
                value={formik.values.contacts}
                onChange={(value) => formik.setFieldValue("contacts", value)}
              />

              <BaseSelect
                fieldTitle="Статус"
                options={statusOptions}
                placeholder="Выберите статус"
                value={formik.values.status}
                onChange={(value) => formik.setFieldValue("status", value)}
              />

              <BaseInput
                fieldTitle="Рабочий телефон"
                value={formik.values.work_phone}
                onChange={handlePhoneChange}
                placeholder="+7 (___) ___-__-__"
                name="work_phone"
              />

              <BaseInput
                fieldTitle="Рабочая почта"
                value={formik.values.work_email}
                onChange={formik.handleChange}
                placeholder="example@company.com"
                name="work_email"
                type="email"
              />

              <BaseInput
                fieldTitle="Сайт"
                value={formik.values.website}
                onChange={formik.handleChange}
                placeholder="https://example.com"
                name="website"
                type="url"
              />

              <BaseInput
                fieldTitle="ИНН"
                value={formik.values.inn}
                onChange={formik.handleChange}
                placeholder="Введите ИНН"
                name="inn"
              />

              <BaseInput
                fieldTitle="Юридическое название"
                value={formik.values.legal_name}
                onChange={formik.handleChange}
                placeholder="Введите юридическое название"
                name="legal_name"
              />

              <BaseInput
                fieldTitle="Количество сотрудников"
                value={formik.values.employees_count}
                onChange={formik.handleChange}
                placeholder="Введите количество"
                name="employees_count"
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <BaseInput
                fieldTitle="Юридический адрес"
                value={formik.values.legal_address}
                onChange={formik.handleChange}
                placeholder="Введите юридический адрес"
                name="legal_address"
              />

              <BaseInput
                fieldTitle="Почтовый адрес"
                value={formik.values.mailing_address}
                onChange={formik.handleChange}
                placeholder="Введите почтовый адрес"
                name="mailing_address"
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <BaseInput
                fieldTitle="Фамилия"
                value={formik.values.director_last_name}
                onChange={formik.handleChange}
                placeholder="Фамилия"
                name="director_last_name"
              />

              <BaseInput
                fieldTitle="Имя"
                value={formik.values.director_first_name}
                onChange={formik.handleChange}
                placeholder="Имя"
                name="director_first_name"
              />

              <BaseInput
                fieldTitle="Отчество"
                value={formik.values.director_middle_name}
                onChange={formik.handleChange}
                placeholder="Отчество"
                name="director_middle_name"
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <BaseControl fieldTitle="Бланк NDA">
                <Flex
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  bgColor="white"
                  height="40px"
                  alignItems="center"
                  justifyContent="center"
                  gap="8px"
                  cursor="pointer"
                  position="relative"
                  _hover={{
                    borderColor: "blue.400",
                  }}
                >
                  <Icon as={ArrowUpIcon} boxSize={4} color="gray.500" />
                  <Text fontSize="sm" color="gray.700">
                    Загрузить файл
                  </Text>
                  <Input
                    type="file"
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    opacity="0"
                    cursor="pointer"
                    onChange={(e) => handleFileChange("nda_blank_file", e)}
                    accept=".pdf,.doc,.docx"
                  />
                </Flex>
                {formik.values.nda_blank_file && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {formik.values.nda_blank_file.name}
                  </Text>
                )}
                {currentPartner.has_nda_blank && !formik.values.nda_blank_file && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Файл загружен
                  </Text>
                )}
              </BaseControl>

              <BaseControl fieldTitle="Скан NDA">
                <Flex
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  bgColor="white"
                  height="40px"
                  alignItems="center"
                  justifyContent="center"
                  gap="8px"
                  cursor="pointer"
                  position="relative"
                  _hover={{
                    borderColor: "blue.400",
                  }}
                >
                  <Icon as={ArrowUpIcon} boxSize={4} color="gray.500" />
                  <Text fontSize="sm" color="gray.700">
                    Загрузить файл
                  </Text>
                  <Input
                    type="file"
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    opacity="0"
                    cursor="pointer"
                    onChange={(e) => handleFileChange("nda_scan_file", e)}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </Flex>
                {formik.values.nda_scan_file && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {formik.values.nda_scan_file.name}
                  </Text>
                )}
                {currentPartner.has_nda_scan && !formik.values.nda_scan_file && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Файл загружен
                  </Text>
                )}
              </BaseControl>

              <BaseSelect
                fieldTitle="Подписано в ЭДО"
                options={signedInEdoOptions}
                placeholder="Выберите"
                value={formik.values.signed_in_edo}
                onChange={(value) => formik.setFieldValue("signed_in_edo", value)}
              />

              <BaseSelect
                fieldTitle="Формат взаимодействия"
                options={interactionFormatOptions}
                placeholder="Выберите формат"
                value={formik.values.interaction_format}
                onChange={(value) => formik.setFieldValue("interaction_format", value)}
              />
            </SimpleGrid>

            <BaseTextArea
              fieldTitle="Комментарий"
              value={formik.values.comment}
              onChange={formik.handleChange}
              placeholder="Дополнительная информация..."
              name="comment"
              rows={4}
            />

            <Flex gap="16px" justifyContent="flex-end" pt="8px">
              <Button variant="outline" onClick={() => (currentPartner ? navigate(`/partners/${currentPartner.id}`) : navigate("/partners"))}>
                Отмена
              </Button>
              <Button
                bg="#2563eb"
                color="white"
                _hover={{ bg: "#2563eb" }}
                _active={{ bg: "#2563eb" }}
                isDisabled={!isFormValid || !hasChanges}
                onClick={() => formik.handleSubmit()}
              >
                Сохранить изменения
              </Button>
            </Flex>
          </Flex>
        </FormWrapper>
      </Flex>
    </Layout>
  );
};

